import { Div, type DivProps, Tooltip, TooltipTrigger } from "@hopper-ui/components";
import { QuestionIcon } from "@hopper-ui/icons";
import { O365GroupsIcon } from "../assets/icons/O365GroupsIcon.tsx";
import { O365OneDriveIcon } from "../assets/icons/O365OneDriveIcon.tsx";
import { O365SharePointIcon } from "../assets/icons/O365SharePointIcon.tsx";
import { O365TeamsChannelIcon } from "../assets/icons/O365TeamsChannelIcon.tsx";
import { O365TeamsIcon } from "../assets/icons/O365TeamsIcon.tsx";
import { type ComponentsTranslationKey, useComponentsTranslation } from "../i18n/i18next.ts";
import { slot } from "../utils/useSlots.tsx";
import type { WorkspaceType } from "./workspaceType.ts";

interface WorkspaceIconProps extends Omit<DivProps, "children"> {
    type: WorkspaceType;
    isDecorative?: boolean;
}

const workspaceTypeKeys = {
    group: { label: "WorkspaceIcon.group", tooltip: "WorkspaceIcon.group" },
    team: { label: "WorkspaceIcon.team", tooltip: "WorkspaceIcon.team" },
    teams: { label: "WorkspaceIcon.team", tooltip: "WorkspaceIcon.team" },
    oneDrive: { label: "WorkspaceIcon.oneDrive", tooltip: "WorkspaceIcon.oneDrive" },
    sharePointSite: { label: "WorkspaceIcon.sharePointSite", tooltip: "WorkspaceIcon.sharePointSite" },
    teamsChannel: { label: "WorkspaceIcon.teamsChannel", tooltip: "WorkspaceIcon.teamsChannel" },
    unknown: { label: "WorkspaceIcon.unknown", tooltip: "WorkspaceIcon.unknownTooltip" }
} as const satisfies Record<WorkspaceType, { label: ComponentsTranslationKey; tooltip: ComponentsTranslationKey }>;

const InnerWorkspaceIcon = ({ type, isDecorative, ...props }: WorkspaceIconProps) => {
    const { t } = useComponentsTranslation();
    const keys = workspaceTypeKeys[type];
    const label = t(keys.label);

    const icon = {
        group: <O365GroupsIcon aria-label={label} />,
        team: <O365TeamsIcon aria-label={label} />,
        teams: <O365TeamsIcon aria-label={label} />,
        oneDrive: <O365OneDriveIcon aria-label={label} />,
        sharePointSite: <O365SharePointIcon aria-label={label} />,
        teamsChannel: <O365TeamsChannelIcon aria-label={label} />,
        unknown: <QuestionIcon aria-label={label} />
    }[type];

    if (isDecorative) {
        return <Div {...props}>{icon}</Div>;
    }

    return (
        <TooltipTrigger>
            <Div {...props}>{icon}</Div>
            <Tooltip>{t(keys.tooltip)}</Tooltip>
        </TooltipTrigger>
    );
};

export const WorkspaceIcon = slot("icon", InnerWorkspaceIcon);
